import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { AlertTriangle, CloudDownload, FileSpreadsheet, FileText, Pencil, Printer, Search, Trash2, Upload } from "lucide-react";
import ExcelImportDialog from "@/components/ExcelImportDialog";
import ExportDialog from "@/components/ExportDialog";
import { Checkbox } from "@/components/ui/checkbox";

const DAY_AR = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const dayName = (d) => {
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? "—" : DAY_AR[dt.getDay()];
};
import api, { downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const STORE_URL = "https://chromewebstore.google.com/detail/dpgeglehccekblhcghkengajjodiofjd";

const STATUS_BADGE = {
  present: { label: "حاضر", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  late: { label: "متأخر", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  absent: { label: "غائب", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  excused: { label: "مستأذن", cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

const ATTENDANCE_FIELDS = [
  { key: "employee_name", label: "اسم الموظف", required: true },
  { key: "check_in", label: "وقت الحضور" },
  { key: "check_out", label: "وقت الانصراف" },
  { key: "employee_id", label: "رقم الهوية" },
];

const ATTENDANCE_GUESS = {
  employee_name: ["الاسم", "اسم الموظف", "اسم المعلم", "الموظف", "المعلم"],
  check_in: ["وقت الحضور", "بصمة الحضور", "وقت البصمة", "البصمة الاولي", "الحضور"],
  check_out: ["وقت الانصراف", "بصمة الانصراف", "البصمة الاخيرة", "الانصراف"],
  employee_id: ["رقم الهوية", "الهوية", "السجل المدني", "السجل"],
};

const readSavedDateFilters = () => {
  try { return JSON.parse(localStorage.getItem("attendance_date_filters") || "{}"); } catch { return {}; }
};

export default function AttendanceTab() {
  const [records, setRecords] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [from, setFrom] = useState(() => readSavedDateFilters().from || dayjs().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(() => readSavedDateFilters().to || dayjs().endOf("month").format("YYYY-MM-DD"));
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("all");
  const [status, setStatus] = useState("all");
  const [alertsOnly, setAlertsOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exportKind, setExportKind] = useState(null);
  const [editRec, setEditRec] = useState(null);
  const [editStatus, setEditStatus] = useState("excused");
  const [selected, setSelected] = useState(() => new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [timeFixAlert, setTimeFixAlert] = useState(null);
  const [extVersion, setExtVersion] = useState(null);
  const [imp, setImp] = useState(null);
  const maxDoneRef = useRef(0); // عدّاد تصاعدي — الشريط لا ينخفض أبداً ضمن الجولة
  const runStartRef = useRef(0); // وقت بدء الجولة الحالية — لتجاهل بقايا الجولات السابقة
  const idleStreakRef = useRef(0); // استطلاعات idle متتالية أثناء جولة ظاهرة — مانع التجميد
  const [askStart, setAskStart] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [schedInfo, setSchedInfo] = useState(null);

  const SCHEDULE_LABELS = { summer: "التوقيت الصيفي", winter: "التوقيت الشتوي",
                            ramadan: "توقيت رمضان", flexible: "التوقيت المرن" };

  useEffect(() => {
    api.get("/settings")
      .then((r) => { setSchedInfo(r.data); setActiveSchedule(r.data.active_schedule || "summer"); })
      .catch(() => {});
  }, []);

  const schedTimeLabel = (k, part) => {
    const v = schedInfo?.[`${k}_work_${part}`];
    return v === "open" ? "مفتوح" : (v || "—");
  };

  const switchSchedule = async (sched) => {
    if (sched === activeSchedule) return;
    const label = SCHEDULE_LABELS[sched];
    if (!window.confirm(`سيتم تفعيل «${label}» وإعادة احتساب التأخير والانصراف المبكر لسجلات الفترة المعروضة فقط (من ${from} إلى ${to}). بقية السجلات خارج الفترة تبقى كما هي، والمساءلات وقرارات الحسم الصادرة سابقاً لا تتغير. متابعة؟`)) return;
    try {
      const { data } = await api.post("/settings/switch-schedule",
        { schedule: sched, from_date: from, to_date: to });
      toast.success(`تم تفعيل ${label} — أُعيد احتساب ${data.recalculated} سجل ضمن الفترة`);
      setActiveSchedule(sched);
      setSchedInfo((p) => (p ? { ...p, active_schedule: sched, work_start: data.work_start, work_end: data.work_end } : p));
      loadRef.current();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذر تبديل التوقيت"); }
  };


const TIME_FIX_MESSAGES = {
  checkout_ignored: "تم تبصيم انصراف اليوم السابق اليوم وتم التصحيح الآلي وتجاهل تبصيم الانصرف لكي لا تحسب عليه دقائق خروج مبكر ذكر الموظف بضرورة تبصيم الانصراف بمنصة حضوري في نهاية اليوم الدراسي",
  checkout_moved: "الموظف بصم اليوم انصراف الأمس ولم يبصم حضوره اليوم وتم التصحيح الآلي باحتساب تبصيم الانصراف حضور لكي لا يسجل غائبا ولكن الموظف يحتاج الى اثبات حضوره يدويا أو التبصيم بمنصة حضوري اذا لا زال الوقت متاح",
};
  const recKey = (r) => `${r.employee_name}|${r.date}`;
  const alertsCount = records.filter((r) => r.time_fix).length;
  const visibleRecords = alertsOnly ? records.filter((r) => r.time_fix) : records;

  useEffect(() => {
    localStorage.setItem("attendance_date_filters", JSON.stringify({ from, to }));
  }, [from, to]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { frm: from, to };
      if (search) params.search = search;
      if (department !== "all") params.department = department;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/attendance/records", { params });
      setRecords(data);
    } catch { toast.error("تعذر تحميل السجل"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [from, to, department, status, search]);

  useEffect(() => {
    api.get("/attendance/departments").then((r) => setDepartments(r.data)).catch(() => {});
  }, []);

  // ===== الاستيراد التلقائي من حضوري عبر الإضافة =====
  const extSupportsAuto = (v) => {
    const p = String(v || "0").split(".").map((x) => parseInt(x, 10) || 0);
    return p[0] > 1 || (p[0] === 1 && (p[1] || 0) >= 15);
  };
  const extVersionRef = useRef(null);

  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window || !ev.data || ev.data.source !== "rased-ext") return;
      if (ev.data.type === "pong") { extVersionRef.current = ev.data.version || ""; setExtVersion(ev.data.version || ""); }
      if (ev.data.type === "import-ack" && !ev.data.ok) {
        if (ev.data.reason === "no-days") toast.info("لا توجد أيام ناقصة — سجل الدوام محدث حتى اليوم");
        else toast.error("تعذر بدء الاستيراد — تأكد من تسجيل الدخول وحدّث الإضافة من سوق كروم");
        setImp(null);
      }
    };
    window.addEventListener("message", onMsg);
    // نبضة دورية حتى تُجيب الإضافة — تلتقط تثبيتها أثناء بقاء الصفحة مفتوحة
    // (عند التثبيت يحقن background.js الجسر في هذا التبويب فيرد فوراً)
    const ping = () => {
      if (!extVersionRef.current) window.postMessage({ source: "rased-web", type: "ping" }, "*");
    };
    ping();
    const pingTimer = setInterval(ping, 3000);
    return () => { window.removeEventListener("message", onMsg); clearInterval(pingTimer); };
  }, []);

  const defaultStartDate = () => {
    const m = dayjs().month() + 1, y = dayjs().year();
    if (m >= 8) return `${y}-08-16`;
    if (m <= 2) return `${y - 1}-12-01`;
    return `${y}-03-15`;
  };

  const fireImport = (total) => {
    window.postMessage({ source: "rased-web", type: "import-start" }, "*");
    runStartRef.current = Date.now(); // بداية جولة جديدة — كل حالة أقدم منها بقايا تُتجاهل
    maxDoneRef.current = 0; // جولة جديدة — تصفير أساس العدّاد التصاعدي
    setImp({ status: "starting", done: 0, total: total || 0 });
  };

  const startAutoImport = async () => {
    // أعد فحص الإضافة (قد تكون ثُبّتت للتو) قبل الحكم بعدم توفرها
    if (!extSupportsAuto(extVersionRef.current)) {
      window.postMessage({ source: "rased-web", type: "ping" }, "*");
      await new Promise((r) => setTimeout(r, 600));
    }
    const v = extVersionRef.current;
    if (!extSupportsAuto(v)) {
      window.open(STORE_URL, "_blank", "noopener,noreferrer");
      toast.error(v
        ? "إصدار إضافة «مستودع حضوري» قديم — فتحنا صفحة الإضافة في سوق كروم لتحديثها، وبعد التحديث أعد الضغط على زر الاستيراد"
        : "إضافة «مستودع حضوري» غير مثبتة — فتحنا صفحة الإضافة في سوق كروم لتثبيتها، وبعد التثبيت أعد الضغط على زر الاستيراد",
        { duration: 8000 });
      return;
    }
    try {
      const { data: plan } = await api.get("/attendance/import-plan");
      if (!plan.start_date) { setStartDate(defaultStartDate()); setAskStart(true); return; }
      if (!plan.days.length) { toast.info("لا توجد أيام ناقصة — سجل الدوام محدث حتى اليوم"); return; }
      fireImport(plan.total);
    } catch { toast.error("تعذر حساب خطة الاستيراد"); }
  };

  const saveStartAndGo = async () => {
    if (!startDate) { toast.error("حدد تاريخ البداية"); return; }
    try {
      const { data: s } = await api.get("/settings");
      await api.put("/settings", { ...s, import_start_date: startDate });
      setAskStart(false);
      const { data: plan } = await api.get("/attendance/import-plan");
      if (!plan.days.length) { toast.info("لا توجد أيام ناقصة من هذا التاريخ"); return; }
      fireImport(plan.total);
    } catch { toast.error("تعذر حفظ تاريخ البداية"); }
  };

  // متابعة تقدم الاستيراد (يرفع من الإضافة إلى الخادم أولاً بأول)
  const impActive = !!imp && imp.status !== "done" && imp.status !== "error";
  // استئناف عرض الشريط عند فتح/العودة للتبويب: الحالة محفوظة في الخادم
  // (لا تختفي بالتنقل بين التبويبات)، والاستيراد يعمل مستقلاً عن الواجهة
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/attendance/import-progress", { params: { peek: 1 } });
        if (cancelled) return;
        if (data.status && data.status !== "idle" && data.status !== "done" && data.status !== "error") {
          // لا استئناف لبقايا قديمة: الجولة الحية ترفع حالتها باستمرار،
          // فالحالة الأقدم من 3 دقائق بقايا جولة انقطعت ولا يُظهر الشريط لأجلها
          const at = data.at ? new Date(data.at).getTime() : 0;
          if (!at || Date.now() - at > 180000) return;
          runStartRef.current = Date.now(); // يفعّل فلتر تجاهل البقايا في الاستطلاع
          maxDoneRef.current = data.done || 0; // استئناف جولة جارية — الأساس من الخادم
          setImp(data);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!impActive) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/attendance/import-progress");
        if (!data.status || data.status === "idle") {
          // جولة ظاهرة لكن الخادم بلا حالة: النتيجة النهائية التُقطت من قارئ
          // آخر (تبويب ثانٍ/إعادة تركيب) أو أُعيد تشغيل الخادم — لا تجميد:
          // بعد 3 استطلاعات idle متتالية نُخفي الشريط ونحدّث السجل
          idleStreakRef.current += 1;
          if (idleStreakRef.current >= 3) { setImp(null); loadRef.current(); }
          return;
        }
        idleStreakRef.current = 0;
        // تجاهل بقايا الجولات السابقة: الخادم يحتفظ بالحالة النهائية للجولة الماضية،
        // والتقاطها هنا كان يقفز بالشريط لنتيجة قديمة ويوقف الاستطلاع فيتجمد الشريط.
        // الحكم بختم الخادم الزمني مقابل بدء هذه الجولة (بسماح دقيقتين لانحراف الساعة)
        if (runStartRef.current && data.at) {
          const at = new Date(data.at).getTime();
          if (at && at < runStartRef.current - 120000) return;
        }
        if (data.status === "done") {
          setImp(data);
          toast.success(`تم استيراد البيانات بنجاح (${data.imported} سجل) — تم تحديث سجل الدوام`, { id: "hudoori-import" });
          loadRef.current();
          setTimeout(() => setImp(null), 6000);
        } else if (data.status === "error") {
          setImp(null);
          toast.error(data.message || "تعذر الاستيراد من حضوري", { id: "hudoori-import" });
        } else if (data.status === "scraping") {
          // الشريط تصاعدي فقط — تحديث متأخر (بعد تسابق الشبكة) لا يخفض العدّاد
          const done = Math.max(data.done || 0, maxDoneRef.current);
          maxDoneRef.current = done;
          setImp({ ...data, done });
        } else {
          maxDoneRef.current = data.done || 0; // مرحلة غير السحب — أساس جديد
          setImp(data);
        }
      } catch { /* transient */ }
    }, 2000);
    return () => clearInterval(t);
  }, [impActive]);

  // auto-refresh: poll for new imports (chrome extension sends straight to
  // the API) and reload the visible log without a page refresh
  const loadRef = useRef(load);
  loadRef.current = load;
  const lastImportRef = useRef(null);
  useEffect(() => {
    let stopped = false;
    const check = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const { data } = await api.get("/attendance/imports");
        const newest = data?.[0];
        if (!newest) return;
        const sig = `${newest.id}|${newest.imported}`;
        if (lastImportRef.current === null) { lastImportRef.current = sig; return; }
        if (sig !== lastImportRef.current) {
          lastImportRef.current = sig;
          if (!stopped) {
            toast.success(
              `استُلمت ${newest.imported} سجل جديد ${newest.source === "chrome-extension" ? "من أداة كروم" : ""} — تم تحديث سجل الدوام`,
              { id: "import-auto-refresh" });
            loadRef.current();
          }
        }
      } catch { /* polling must stay silent */ }
    };
    check();
    const t = setInterval(check, 15000);
    return () => { stopped = true; clearInterval(t); };
  }, []);

  const exportReport = async (kind, names) => {
    if (names === undefined) { setExportKind(kind); return; }
    try {
      const emp = names ? `&employees=${encodeURIComponent(names.join(","))}` : "";
      await downloadFile(`/export/${kind}?report=attendance&frm=${from}&to=${to}${emp}`,
        `تقرير-الدوام-الرسمی-${from}.${kind === "excel" ? "xlsx" : "pdf"}`);
      toast.success("تم تصدير التقرير");
      setExportKind(null);
    } catch { toast.error("فشل التصدير"); }
  };

  const saveStatus = async () => {
    try {
      await api.put("/attendance/record", {
        employee_name: editRec.employee_name, date: editRec.date, status: editStatus,
      });
      toast.success("تم تحديث حالة السجل");
      setEditRec(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التحديث");
    }
  };

  const deleteAllRecords = async () => {
    if (!window.confirm("تنبيه: سيتم حذف جميع بيانات سجل الدوام نهائياً. هل أنت متأكد؟")) return;
    if (!window.confirm("تأكيد أخير: لا يمكن التراجع عن هذا الإجراء. متابعة حذف كل السجلات؟")) return;
    try {
      const { data } = await api.delete("/attendance/records");
      toast.success(`تم حذف ${data.deleted} سجل`);
      setSelected(new Set());
      setDeleteOpen(false);
      load();
    } catch { toast.error("فشل الحذف"); }
  };

  const toggleSelect = (r) => {
    const k = recKey(r);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => (visibleRecords.length > 0 && prev.size === visibleRecords.length
      ? new Set() : new Set(visibleRecords.map(recKey))));
  };

  const bulkDelete = async (mode) => {
    const targets = mode === "selected" ? records.filter((r) => selected.has(recKey(r))) : visibleRecords;
    if (!targets.length) return;
    setDeleting(true);
    try {
      let deleted = 0;
      for (let i = 0; i < targets.length; i += 1000) {
        const { data } = await api.post("/attendance/records/bulk-delete", {
          keys: targets.slice(i, i + 1000).map((r) => ({ employee_name: r.employee_name, date: r.date, employee_id: r.employee_id || "" })),
        });
        deleted += data.deleted;
      }
      toast.success(`تم حذف ${deleted} سجل نهائياً`);
      setSelected(new Set());
      setDeleteOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحذف");
    } finally { setDeleting(false); }
  };

  return (
    <div className="bg-white rounded-2xl border shadow-sm" data-testid="attendance-tab">
      <ExportDialog open={!!exportKind} onClose={() => setExportKind(null)}
        onConfirm={(names) => exportReport(exportKind, names)} title="تقرير الدوام الرسمي" />
      <div className="p-4 border-b flex flex-wrap items-center gap-3">
        {activeSchedule && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" data-testid="badge-active-schedule"
                title="انقر للتبديل بين التوقيتات — يعيد احتساب كل السجلات"
                className="text-[11px] font-bold text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 whitespace-nowrap hover:bg-emerald-100 transition-colors cursor-pointer">
                {SCHEDULE_LABELS[activeSchedule] || "التوقيت الصيفي"} ▾
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" dir="rtl" data-testid="schedule-switch-menu">
              {["summer", "winter", "ramadan", "flexible"].map((k) => (
                <DropdownMenuItem key={k} data-testid={`switch-schedule-${k}`}
                  onSelect={() => switchSchedule(k)}
                  className={`text-xs flex flex-col items-start gap-0.5 cursor-pointer ${activeSchedule === k ? "bg-emerald-50 font-bold" : ""}`}>
                  <span>{SCHEDULE_LABELS[k]}{activeSchedule === k ? " ✓" : ""}</span>
                  <span className="text-[10px] text-slate-400" dir="rtl">
                    {schedTimeLabel(k, "start")} – {schedTimeLabel(k, "end")}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <div className="flex items-center gap-2">
          <DateInput type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            data-testid="filter-date-from" className="w-36 h-9 text-xs" dir="ltr" />
          <span className="text-xs text-slate-400">إلى</span>
          <DateInput type="date" value={to} onChange={(e) => setTo(e.target.value)}
            data-testid="filter-date-to" className="w-36 h-9 text-xs" dir="ltr" />
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute right-2.5 top-2.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث بالاسم أو رقم الهوية" data-testid="filter-search-employee"
            className="pr-8 w-56 h-9 text-xs" />
        </div>
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger data-testid="filter-department-select" className="w-44 h-9 text-xs">
            <SelectValue placeholder="القسم" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الأقسام</SelectItem>
            {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="filter-status-select" className="w-36 h-9 text-xs">
            <SelectValue placeholder="الحالة" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الحالات</SelectItem>
            <SelectItem value="present">حاضر</SelectItem>
            <SelectItem value="late">متأخر</SelectItem>
            <SelectItem value="absent">غائب</SelectItem>
            <SelectItem value="excused">مستأذن</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant={alertsOnly ? "default" : "outline"}
          onClick={() => setAlertsOnly((v) => !v)}
          data-testid="filter-alerts-only"
          className={`h-9 text-xs gap-1.5 ${alertsOnly
            ? "bg-amber-600 hover:bg-amber-700 text-white border-amber-600"
            : "border-amber-300 text-amber-800 hover:bg-amber-50"}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> ذات تنبيهات فقط{alertsCount > 0 ? ` (${alertsCount})` : ""}
        </Button>
        <div className="mr-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setDeleteOpen(true)}
            disabled={visibleRecords.length === 0}
            data-testid="btn-delete-records"
            className="h-9 text-xs gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50">
            <Trash2 className="w-4 h-4" /> حذف السجلات{selected.size > 0 ? ` (${selected.size})` : ""}
          </Button>
          <Button size="sm" onClick={startAutoImport}
            data-testid="btn-import-hudoori"
            className="h-9 text-xs gap-1.5 bg-emerald-900 hover:bg-emerald-950 text-white">
            <CloudDownload className="w-4 h-4" /> استيراد من حضوري
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}
            data-testid="btn-import-attendance-excel" className="h-9 text-xs gap-1.5">
            <Upload className="w-4 h-4 text-blue-700" /> استيراد Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportReport("excel")}
            data-testid="btn-export-excel" className="h-9 text-xs gap-1.5">
            <FileSpreadsheet className="w-4 h-4 text-emerald-700" /> Excel
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportReport("pdf")}
            data-testid="btn-export-pdf" className="h-9 text-xs gap-1.5">
            <FileText className="w-4 h-4 text-rose-700" /> PDF
          </Button>
          <Button size="sm" variant="outline"
            onClick={() => downloadFile(`/export/pdf?report=daily_log&frm=${from}&to=${to}`, "السجل-اليومي.pdf")}
            data-testid="btn-print-daily-log" className="h-9 text-xs gap-1.5">
            <Printer className="w-4 h-4 text-slate-600" /> طباعة سجل يومي
          </Button>
        </div>
      </div>

      {imp && (
        <div className="p-4 border-b bg-blue-50/50" data-testid="hudoori-import-progress">
          <div className="flex items-center justify-between mb-1.5 text-xs">
            <span className="font-bold text-blue-900" data-testid="hudoori-import-status">
              {imp.status === "starting" && "جاري بدء الاستيراد من حضوري..."}
              {imp.status === "boot" && "جاري فتح منصة حضوري..."}
              {imp.status === "waiting-login" && "بانتظار تسجيل الدخول في منصة حضوري (النافذة المنبثقة)..."}
              {imp.status === "navigating" && "تم تسجيل الدخول — جاري فتح تقرير الحضور اليومي..."}
              {imp.status === "scraping" && "جاري الاستيراد من حضوري..."}
              {imp.status === "done" && "تم استيراد البيانات بنجاح"}
            </span>
            {imp.total > 0 && (
              <span className="text-blue-700 font-mono" data-testid="hudoori-import-count">
                {Math.min(imp.done, imp.total)} / {imp.total} يوم
              </span>
            )}
          </div>
          {imp.total > 0 && (
            <div className="h-2.5 bg-blue-100 rounded-full overflow-hidden" dir="ltr">
              <div className="h-full bg-blue-600 rounded-full transition-all duration-500"
                data-testid="hudoori-import-bar"
                style={{ width: `${Math.min(100, Math.round((imp.done / imp.total) * 100))}%` }} />
            </div>
          )}
          {imp.day && (
            <p className="text-[11px] text-blue-500 mt-1" data-testid="hudoori-import-day">اليوم الجاري: {imp.day}</p>
          )}
          {imp.message && imp.status !== "error" && (
            <p className="text-[11px] text-blue-600 mt-1 font-semibold" data-testid="hudoori-import-step">
              الخطوة الحالية: {imp.message}
            </p>
          )}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="table-attendance-log">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs">
              <th className="px-3 py-3 w-10">
                <Checkbox
                  checked={visibleRecords.length > 0 && selected.size === visibleRecords.length}
                  onCheckedChange={toggleSelectAll}
                  data-testid="check-select-all-records"
                  aria-label="تحديد كل السجلات الظاهرة" />
              </th>
              {["التاريخ", "اليوم", "اسم الموظف", "رقم الهوية", "الحضور", "الانصراف", "التأخير", "خروج مبكر", "الحالة", "استئذان"].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap text-center">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="text-center py-10 text-slate-400">جارٍ التحميل...</td></tr>}
            {!loading && visibleRecords.length === 0 && (
              <tr><td colSpan={11} className="text-center py-10 text-slate-400">
                {alertsOnly && records.length > 0
                  ? "لا توجد سجلات بتنبيهات في الفترة المحددة"
                  : "لا توجد سجلات — استورد البيانات من إضافة كروم أو ملف Excel"}
              </td></tr>
            )}
            {!loading && visibleRecords.map((r, i) => {
              const badge = STATUS_BADGE[r.status] || STATUS_BADGE.present;
              const newDay = i === 0 || visibleRecords[i - 1].date !== r.date;
              return [
                newDay && (
                  <tr key={`day-sep-${r.date}`} data-testid={`day-separator-${r.date}`}
                    className="bg-emerald-50/80 border-t-2 border-emerald-200">
                    <td colSpan={11} className="px-4 py-1.5 text-[11px] font-bold text-emerald-900 whitespace-nowrap">
                      {dayName(r.date)} <span dir="ltr" className="font-mono text-emerald-700">{r.date}</span>
                    </td>
                  </tr>
                ),
                <tr key={`${r.employee_name}-${r.date}`} className={`border-t hover:bg-slate-50/60 ${selected.has(recKey(r)) ? "bg-orange-50/50" : ""}`}>
                  <td className="px-3 py-2.5 text-center">
                    <Checkbox checked={selected.has(recKey(r))} onCheckedChange={() => toggleSelect(r)}
                      data-testid={`check-record-${r.employee_name}-${r.date}`}
                      aria-label={`تحديد سجل ${r.employee_name}`} />
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-center" dir="ltr">{r.date}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 text-center whitespace-nowrap" data-testid={`day-cell-${r.employee_name}-${r.date}`}>{dayName(r.date)}</td>
                  <td className="px-4 py-2.5 font-semibold whitespace-nowrap text-center">
                    <span className="inline-flex items-center gap-1.5">
                      {r.employee_name}
                      {r.time_fix && TIME_FIX_MESSAGES[r.time_fix] && (
                        <button type="button"
                          onClick={() => setTimeFixAlert({ name: r.employee_name, msg: TIME_FIX_MESSAGES[r.time_fix] })}
                          data-testid={`time-fix-badge-${r.employee_name}-${r.date}`}
                          className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 border transition-colors ${
                            r.time_fix === "checkout_moved"
                              ? "text-rose-800 bg-rose-100 border-rose-300 hover:bg-rose-200"
                              : "text-amber-800 bg-amber-100 border-amber-300 hover:bg-amber-200"}`}>
                          <AlertTriangle className="w-3 h-3" /> تنبيه
                        </button>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-center">{r.employee_id || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-center" dir="ltr">{r.check_in || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-center" dir="ltr">{r.check_out || "—"}</td>
                  <td className="px-4 py-2.5 text-center">
                    {r.late_minutes > 0
                      ? <span className="text-amber-700 font-bold text-xs">{r.late_minutes} د</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {r.early_minutes > 0
                      ? <span className="text-orange-700 font-bold text-xs">{r.early_minutes} د</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-[11px] font-semibold border rounded-full px-2.5 py-0.5 ${badge.cls}`}>
                      {badge.label}
                    </span>
                    {r.manual_override && <span className="text-[10px] text-blue-500 mr-1">(يدوي)</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <button onClick={() => { setEditRec(r); setEditStatus("excused"); }}
                      data-testid={`btn-edit-status-${r.employee_name}-${r.date}`}
                      title="تسجيل استئذان / تعديل الحالة"
                      className="text-slate-400 hover:text-emerald-800 p-1.5">
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>,
              ];
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={askStart} onOpenChange={setAskStart}>
        <DialogContent className="max-w-sm" data-testid="import-start-dialog">
          <DialogHeader>
            <DialogTitle>تاريخ بداية الاستيراد</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 leading-6">
            من أي تاريخ تريد بدء جلب بيانات الدوام من حضوري؟ سيُستورد من هذا التاريخ حتى اليوم،
            وفي المرات القادمة تُجلب الأيام الناقصة فقط تلقائياً.
          </p>
          <DateInput value={startDate} onChange={(e) => setStartDate(e.target.value)}
            data-testid="import-start-date" className="h-10" />
          <Button onClick={saveStartAndGo} data-testid="btn-confirm-import-start"
            className="w-full bg-emerald-900 hover:bg-emerald-950 text-white">
            حفظ وبدء الاستيراد
          </Button>
        </DialogContent>
      </Dialog>
      <ExcelImportDialog open={importOpen} onOpenChange={setImportOpen}
        title="استيراد سجل الدوام من ملف Excel"
        description="اختر ملف Excel لسجل الحضور بأي ترتيب أعمدة — في الخطوة التالية ستحدد أي عمود يقابل أي حقل (الاسم إلزامي، والباقي اختياري) وستُدخل تاريخ الدوام يدوياً ليُطبَّق على جميع الصفوف."
        fields={ATTENDANCE_FIELDS} guessMap={ATTENDANCE_GUESS}
        previewUrl="/attendance/excel-preview" importUrl="/attendance/excel-import"
        withManualDate
        onDone={(d) => {
          if (d?.date_from && d?.date_to) {
            setFrom((cur) => (d.date_from < cur ? d.date_from : cur));
            setTo((cur) => (d.date_to > cur ? d.date_to : cur));
          }
          load();
        }}
        testIdPrefix="attendance-import" />

      <Dialog open={!!timeFixAlert} onOpenChange={() => setTimeFixAlert(null)}>
        <DialogContent className="max-w-md" data-testid="time-fix-alert-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5 shrink-0" /> تنبيه تصحيح آلي — {timeFixAlert?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-7 text-slate-700" data-testid="time-fix-alert-message">{timeFixAlert?.msg}</p>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" dir="rtl" data-testid="bulk-delete-modal">
          <DialogHeader><DialogTitle>حذف سجلات الدوام</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500 leading-6">
              اختر نطاق الحذف: السجلات المحددة، أو كل الظاهر على الشاشة وفق التصفية
              (من {from} إلى {to})، أو جميع سجلات الدوام نهائياً. الحذف نهائي ولا يمكن التراجع عنه.
            </p>
            <div className="space-y-2">
              <Button onClick={() => bulkDelete("selected")} disabled={deleting || selected.size === 0}
                data-testid="btn-confirm-delete-selected"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white gap-1.5">
                <Trash2 className="w-4 h-4" />
                حذف السجلات المحددة ({selected.size})
              </Button>
              <Button onClick={() => bulkDelete("visible")} disabled={deleting || visibleRecords.length === 0}
                data-testid="btn-confirm-delete-visible" variant="outline"
                className="w-full border-rose-300 text-rose-700 hover:bg-rose-50 gap-1.5">
                <Trash2 className="w-4 h-4" />
                حذف كل السجلات الظاهرة ({visibleRecords.length})
              </Button>
              <Button onClick={deleteAllRecords} disabled={deleting}
                data-testid="btn-confirm-delete-all"
                className="w-full bg-rose-600 hover:bg-rose-700 text-white gap-1.5">
                <Trash2 className="w-4 h-4" /> حذف جميع السجلات نهائياً
              </Button>
            </div>
            {deleting && <p className="text-xs text-slate-400 text-center">جارٍ الحذف...</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRec} onOpenChange={() => setEditRec(null)}>
        <DialogContent className="max-w-md" dir="rtl" data-testid="edit-status-modal">
          <DialogHeader><DialogTitle>تعديل حالة السجل يدوياً</DialogTitle></DialogHeader>
          {editRec && (
            <div className="space-y-4">
              <div className="bg-slate-50 border rounded-xl p-3 text-sm">
                <p className="font-bold">{editRec.employee_name}</p>
                <p className="text-xs text-slate-500" dir="ltr">{editRec.date}</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  بيانات الاستئذان غير متوفرة في منصة حضوري — يمكنك تسجيلها هنا يدوياً
                </p>
              </div>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger data-testid="edit-status-select" className="h-10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excused">مستأذن</SelectItem>
                  <SelectItem value="present">حاضر</SelectItem>
                  <SelectItem value="late">متأخر</SelectItem>
                  <SelectItem value="absent">غائب</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={saveStatus} data-testid="btn-save-status"
                className="w-full bg-emerald-900 hover:bg-emerald-950 text-white">حفظ</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
